import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  Target, Database, Brain, Settings, Users, Shield,
  ChevronRight, ChevronLeft, CheckCircle, AlertCircle, AlertTriangle,
  Loader2, Building2, Mail, User, Briefcase, DollarSign,
  TrendingUp, Zap, Clock, Download, ArrowRight, Home,
  Lightbulb, Award, Rocket, PieChart, Activity, Bot,
  MessageSquare, Send, ChevronDown, ChevronUp, Sparkles,
  Layers, Cpu, Radio, CircuitBoard, FileText, ExternalLink, Globe,
  ArrowUpRight, Calendar, UserPlus, Coins, Flag
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { ReportSecurityNote } from '../components/ReportSecurityNote';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import axios from 'axios';
import { submitMaturity } from '../utils/maturityJobs';
import { aiBenchmark, gapText, metricNumber } from '../utils/maturityMetrics';
import { generateAIMaturityPptx } from '../utils/generateAIMaturityPptx';
import AGIInsightsTab from '../components/agi/AGIInsightsTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Industries - all 33 supported industries
const industries = [
  'Aerospace & Defense', 'Agriculture', 'Automotive', 'Biotechnology', 'Chemicals',
  'Consumer Packaged Goods', 'Cosmetics & Personal Care', 'Education', 'Energy',
  'EPC (Engineering, Procurement & Construction)',
  'Financial Services', 'Food Processing', 'Healthcare', 'Insurance', 'Manufacturing',
  'Media & Entertainment', 'Medical Devices', 'Metals & Mining', 'Nonprofit', 'Oil & Gas',
  'Pharmaceuticals', 'Private Equity', 'Professional Services', 'Pulp & Paper',
  'Real Estate & Construction', 'Restaurant', 'Retail', 'Social Sector & NGOs', 'Software',
  'Technology', 'Telecommunications', 'Travel & Logistics', 'Utilities', 'Waste Management', 'Wine, Beer & Spirits'
];

// AI Maturity Level colors and info
const maturityLevelConfig = {
  0: { color: 'from-gray-500 to-gray-600', bg: 'bg-gray-100', text: 'text-gray-700', name: 'Foundation' },
  1: { color: 'from-blue-500 to-blue-600', bg: 'bg-blue-100', text: 'text-blue-700', name: 'Predictive' },
  2: { color: 'from-purple-500 to-purple-600', bg: 'bg-purple-100', text: 'text-purple-700', name: 'Generative' },
  3: { color: 'from-emerald-500 to-emerald-600', bg: 'bg-emerald-100', text: 'text-emerald-700', name: 'Agentic' },
  4: { color: 'from-amber-500 to-amber-600', bg: 'bg-amber-100', text: 'text-amber-700', name: 'Multi-Agent' },
  5: { color: 'from-red-500 to-red-600', bg: 'bg-red-100', text: 'text-red-700', name: 'Physical' }
};

// Icon mapping for dimensions
const dimensionIcons = {
  'Target': Target,
  'Database': Database,
  'Brain': Brain,
  'Settings': Settings,
  'Users': Users,
  'Shield': Shield
};

const AIMaturityPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const assessmentIdFromUrl = searchParams.get('id');
  
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingAssessment, setLoadingAssessment] = useState(false);
  const [questionsData, setQuestionsData] = useState(null);
  const [responses, setResponses] = useState({});
  const [results, setResults] = useState(null);
  const [fullResults, setFullResults] = useState(null);
  const [error, setError] = useState('');
  const [secretCode, setSecretCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [codeError, setCodeError] = useState('');
  const [assessmentId, setAssessmentId] = useState(assessmentIdFromUrl || null);
  
  // Additional context state (simple textarea, not chatbot)
  const [showContextInput, setShowContextInput] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  
  // Chatbot state (for floating chatbot on earlier steps)
  const [showChatbot, setShowChatbot] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatSessionId, setChatSessionId] = useState('');
  const chatEndRef = useRef(null);
  
  // Profile form state
  const [profile, setProfile] = useState({
    company_name: '',
    company_url: '',
    industry: '',
    company_size: '',
    annual_revenue: '',
    respondent_name: '',
    respondent_role: '',
    respondent_email: ''
  });
  
  // URL scraping state
  const [scrapingUrl, setScrapingUrl] = useState(false);
  const [scrapedData, setScrapedData] = useState(null);

  // Fetch questions on mount
  useEffect(() => {
    fetchQuestions();
    setChatSessionId(`ai-chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  }, []);
  
  // Load assessment from URL if id parameter is present
  useEffect(() => {
    if (assessmentIdFromUrl) {
      loadAssessmentFromUrl(assessmentIdFromUrl);
    }
  }, [assessmentIdFromUrl]);

  // Scroll chat to bottom
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);
  
  const loadAssessmentFromUrl = async (id) => {
    setLoadingAssessment(true);
    try {
      const res = await axios.get(`${API}/ai-maturity/assessment/${id}`);
      if (res.data) {
        setAssessmentId(id);
        const assessment = res.data;
        // Check both code_verified and is_sample_preview flags
        const isSamplePreview = assessment.is_sample_preview === true;
        const isVerified = assessment.code_verified === true || !isSamplePreview;
        
        // Store email in profile for verification
        if (assessment.respondent_email) {
          setProfile(prev => ({ ...prev, respondent_email: assessment.respondent_email }));
        }
        
        setResults({
          success: true,
          assessment_id: id,
          id: id,
          respondent_email: assessment.respondent_email,
          requires_secret_code: !isVerified,
          is_sample_preview: isSamplePreview,
          sample_results: {
            company_name: assessment.company_name,
            industry: assessment.industry,
            overall_score: assessment.overall_score,
            maturity_level: assessment.maturity_level || assessment.overall_maturity_level,
            industry_benchmark: assessment.industry_benchmark?.avg || 2.5,
            top_performer_score: assessment.industry_benchmark?.top || 4.0,
            dimension_scores: assessment.dimension_scores || [],
            total_dimensions_count: assessment.total_dimensions_count || assessment.dimension_scores?.length || 6,
            executive_summary_preview: (assessment.ai_analysis?.executive_summary || '').substring(0, 300) + '...'
          }
        });
        
        // Only set full results if verified and not a sample preview
        if (isVerified && !isSamplePreview) {
          setFullResults(assessment);
        }
        
        setStep(999);
      }
    } catch (err) {
      console.error('Error loading assessment:', err);
      setError('Assessment not found or has expired. Please start a new assessment.');
    } finally {
      setLoadingAssessment(false);
    }
  };

  const fetchQuestions = async () => {
    try {
      const res = await axios.get(`${API}/ai-maturity/questions`);
      setQuestionsData(res.data);
    } catch (err) {
      console.error('Failed to fetch questions:', err);
      setError('Failed to load assessment questions');
    }
  };

  const dimensions = questionsData?.dimensions || [];
  const totalSteps = dimensions.length + 2; // intro + profile + dimensions
  const currentDimensionIndex = step - 2;
  const isLastDimension = step === dimensions.length + 1;
  const progressPercent = ((step) / totalSteps) * 100;

  const validateProfile = () => {
    const required = ['company_name', 'industry', 'company_size', 'annual_revenue', 'respondent_name', 'respondent_role', 'respondent_email'];
    for (const field of required) {
      if (!profile[field]) {
        setError(`Please fill in all required fields`);
        return false;
      }
    }
    if (!profile.respondent_email.includes('@')) {
      setError('Please enter a valid email address');
      return false;
    }
    setError('');
    return true;
  };

  // Scrape company URL for AI strategy reports
  const scrapeCompanyUrl = async () => {
    if (!profile.company_url) return;
    
    setScrapingUrl(true);
    try {
      const formData = new FormData();
      formData.append('url', profile.company_url);
      formData.append('report_type', 'ai_strategy');
      
      const res = await axios.post(`${API}/scrape-company-report`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000
      });
      
      setScrapedData(res.data);
    } catch (err) {
      console.error('Error scraping URL:', err);
      setScrapedData({ success: false, error: 'Failed to analyze URL' });
    } finally {
      setScrapingUrl(false);
    }
  };

  const handleNext = () => {
    if (step === 1 && !validateProfile()) return;
    setStep(prev => prev + 1);
    setError('');
  };

  const handleBack = () => {
    setStep(prev => prev - 1);
    setError('');
  };

  // Chatbot functions
  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('message', userMessage);
      formData.append('session_id', chatSessionId);
      formData.append('company_name', profile.company_name || '');
      formData.append('industry', profile.industry || '');
      formData.append('context', chatMessages.map(m => `${m.role}: ${m.content}`).join('\n'));
      
      const res = await axios.post(`${API}/ai-maturity/chat`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.data.response }]);
    } catch (err) {
      setChatMessages(prev => [...prev, { role: 'assistant', content: 'I apologize, I encountered an error. Please try again.' }]);
    } finally {
      setChatLoading(false);
    }
  };

  const submitAssessment = async () => {
    setLoading(true);
    setError('');
    
    try {
      const formData = new FormData();
      formData.append('company_name', profile.company_name);
      formData.append('industry', profile.industry);
      formData.append('company_size', profile.company_size);
      formData.append('annual_revenue', profile.annual_revenue);
      formData.append('respondent_name', profile.respondent_name);
      formData.append('respondent_role', profile.respondent_role);
      formData.append('respondent_email', profile.respondent_email);
      formData.append('responses', JSON.stringify(responses));
      
      // Include additional context if provided (simple textarea input)
      if (additionalContext.trim()) {
        formData.append('additional_context', additionalContext.trim());
      }
      
      // Include chatbot context if available (from floating chatbot on earlier steps)
      if (chatMessages.length > 0) {
        const chatContext = chatMessages.map(m => `${m.role === 'user' ? 'User' : 'AI Consultant'}: ${m.content}`).join('\n\n');
        formData.append('chatbot_context', chatContext);
      }
      
      // Include scraped URL data if available
      if (profile.company_url) {
        formData.append('company_url', profile.company_url);
      }
      if (scrapedData?.success && scrapedData?.extracted_info) {
        formData.append('scraped_data', JSON.stringify(scrapedData.extracted_info));
      }

      const res = await submitMaturity(`${API}/ai-maturity/assess`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 180000
      });

      setResults(res.data);
      setStep(totalSteps);
    } catch (err) {
      console.error('Assessment error:', err);
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Failed to submit assessment. Please try again.';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || errorMessage;
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || errorMessage;
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async () => {
    if (!secretCode || secretCode.length !== 6) {
      setCodeError('Please enter a valid 6-digit code');
      return;
    }
    
    setVerifyingCode(true);
    setCodeError('');
    
    try {
      const formData = new FormData();
      formData.append('assessment_id', results.id || results.assessment_id);
      // Use profile email, fall back to results email
      const email = profile.respondent_email || results.respondent_email;
      formData.append('email', email);
      formData.append('code', secretCode);
      
      const res = await axios.post(`${API}/ai-maturity/verify-code`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      if (res.data.success) {
        setFullResults(res.data.full_results);
      }
    } catch (err) {
      // Handle different error response formats
      const detail = err.response?.data?.detail;
      let errorMessage = 'Invalid code. Please try again.';
      
      if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (Array.isArray(detail)) {
        // Pydantic validation error
        errorMessage = detail.map(e => e.msg || e.message).join(', ') || 'Validation error. Please check your input.';
      } else if (detail && typeof detail === 'object') {
        errorMessage = detail.msg || detail.message || 'Invalid code. Please try again.';
      }
      
      setCodeError(errorMessage);
    } finally {
      setVerifyingCode(false);
    }
  };

  // Get score status color - fixed thresholds
  const getScoreStatus = (score, benchmarkAvg) => {
    if (metricNumber(score) === null || metricNumber(benchmarkAvg) === null) return { status: 'No benchmark', color: 'text-gray-600', bg: 'bg-gray-100' };
    if (!benchmarkAvg) benchmarkAvg = 2.5; // Default benchmark
    const gap = score - benchmarkAvg;
    
    // More accurate thresholds
    if (gap < -0.8) return { status: 'Critical Gap', color: 'text-red-600', bg: 'bg-red-100', icon: AlertCircle };
    if (gap < -0.3) return { status: 'Below Average', color: 'text-amber-600', bg: 'bg-amber-100', icon: AlertTriangle };
    if (gap < 0.3) return { status: 'At Par', color: 'text-yellow-600', bg: 'bg-yellow-100', icon: Target };
    return { status: 'Above Average', color: 'text-green-600', bg: 'bg-green-100', icon: CheckCircle };
  };

  // Helper to format benchmark comparison text
  const formatBenchmarkText = (benchmarkData) => {
    if (!benchmarkData) return null;
    if (typeof benchmarkData === 'string') return benchmarkData;
    
    // Extract meaningful text from object
    const parts = [];
    if (benchmarkData.industry_average_position) parts.push(benchmarkData.industry_average_position);
    if (benchmarkData.gap_analysis_to_top_performers) parts.push(benchmarkData.gap_analysis_to_top_performers);
    if (benchmarkData.competitive_implications) parts.push(benchmarkData.competitive_implications);
    if (benchmarkData.competitive_position) parts.push(benchmarkData.competitive_position);
    if (benchmarkData.gap_analysis) parts.push(benchmarkData.gap_analysis);
    if (benchmarkData.industry_specific_ai_adoption_trends) parts.push(`Industry Trends: ${benchmarkData.industry_specific_ai_adoption_trends}`);
    
    if (parts.length > 0) return parts.join(' ');
    
    // If it's an object with no recognized keys, try to extract text
    const values = Object.values(benchmarkData).filter(v => typeof v === 'string');
    return values.join(' ') || null;
  };

  // Generate default roadmap phases if not provided
  const getDefaultRoadmap = (industry, score) => {
    const targetLevel = Math.min(5, Math.ceil(score) + 1);
    return [
      {
        phase_name: 'Phase 1: Foundation',
        duration: 'Months 1-3',
        focus_areas: ['Data Infrastructure Assessment', 'AI Strategy Development', 'Team Capability Building'],
        key_initiatives: [
          'Conduct comprehensive AI readiness audit',
          'Define AI governance framework and policies',
          'Identify quick-win AI use cases for pilot projects',
          'Establish data quality baseline and improvement plan'
        ],
        investment_range: '$50K - $150K'
      },
      {
        phase_name: 'Phase 2: Build',
        duration: 'Months 4-6',
        focus_areas: ['AI Pilot Projects', 'MLOps Setup', 'Skills Development'],
        key_initiatives: [
          'Launch 2-3 pilot AI projects in high-impact areas',
          'Implement MLOps pipeline for model deployment',
          'Train core team on AI/ML best practices',
          'Establish model monitoring and governance processes'
        ],
        investment_range: '$100K - $300K'
      },
      {
        phase_name: 'Phase 3: Scale',
        duration: 'Months 7-9',
        focus_areas: ['Production Deployment', 'Process Integration', 'Change Management'],
        key_initiatives: [
          'Scale successful pilots to production systems',
          'Integrate AI into core business processes',
          'Develop AI center of excellence structure',
          'Implement comprehensive AI risk management'
        ],
        investment_range: '$150K - $400K'
      },
      {
        phase_name: 'Phase 4: Optimize',
        duration: 'Months 10-12',
        focus_areas: ['Performance Optimization', 'Innovation Pipeline', 'Continuous Improvement'],
        key_initiatives: [
          'Optimize model performance and reduce inference costs',
          'Establish AI innovation pipeline for new use cases',
          'Implement advanced analytics and monitoring',
          `Target Level ${targetLevel} maturity capabilities`
        ],
        investment_range: '$100K - $250K'
      }
    ];
  };

  // Render intro page
  const renderIntro = () => (
    <div className="max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-12">
        <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500 mb-6 shadow-lg">
          <Brain className="w-10 h-10 text-white" />
        </div>
        <h1 className="text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mb-4">
          AI Maturity Assessment
        </h1>
        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
          Evaluate your organization&apos;s AI readiness across 6 maturity levels - from foundational data platforms to physical AI systems.
        </p>
      </div>

      {/* AI Maturity Ladder */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl p-8 mb-8 text-white">
        <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
          <Layers className="w-5 h-5 text-purple-400" />
          The AI Maturity Ladder
        </h2>
        <div className="space-y-3">
          {[
            { level: 5, name: 'Physical AI', desc: 'AI that senses, moves, and acts in the real world', icon: Radio, color: 'from-red-500 to-red-600' },
            { level: 4, name: 'Agentic AI', desc: 'Multi-agent coordination with feedback loops', icon: CircuitBoard, color: 'from-amber-500 to-amber-600' },
            { level: 3, name: 'AI Agents', desc: 'Task decomposition and tool orchestration', icon: Bot, color: 'from-emerald-500 to-emerald-600' },
            { level: 2, name: 'Generative AI', desc: 'Reasoning, synthesis, and knowledge creation', icon: Sparkles, color: 'from-purple-500 to-purple-600' },
            { level: 1, name: 'Predictive AI', desc: 'Using historical data to forecast outcomes', icon: TrendingUp, color: 'from-blue-500 to-blue-600' },
            { level: 0, name: 'AI-Ready Data Platform', desc: 'The foundation for AI success', icon: Database, color: 'from-gray-500 to-gray-600' }
          ].map((item) => {
            const Icon = item.icon;
            return (
              <div key={item.level} className="flex items-center gap-4 p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-colors">
                <div className={`w-10 h-10 rounded-lg bg-gradient-to-r ${item.color} flex items-center justify-center flex-shrink-0`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-gray-400">L{item.level}</span>
                    <span className="font-semibold">{item.name}</span>
                  </div>
                  <p className="text-sm text-gray-400">{item.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Assessment Overview */}
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
            <Target className="w-6 h-6 text-indigo-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">6 Dimensions</h3>
          <p className="text-sm text-gray-600">Comprehensive assessment across strategy, data, models, ops, adoption & risk</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center mx-auto mb-4">
            <Bot className="w-6 h-6 text-purple-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">AI Consultant</h3>
          <p className="text-sm text-gray-600">Chat with our AI to provide additional context for personalized insights</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-xl p-6 text-center hover:shadow-lg transition-shadow">
          <div className="w-12 h-12 rounded-full bg-pink-100 flex items-center justify-center mx-auto mb-4">
            <PieChart className="w-6 h-6 text-pink-600" />
          </div>
          <h3 className="font-semibold text-gray-900 mb-2">Industry Benchmarks</h3>
          <p className="text-sm text-gray-600">Compare your AI maturity against industry average and top performers</p>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <Button
          onClick={() => setStep(1)}
          className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 hover:from-indigo-700 hover:via-purple-700 hover:to-pink-700 text-white px-8 py-6 text-lg rounded-xl shadow-lg"
          data-testid="btn-start"
        >
          Start Assessment
          <ChevronRight className="w-5 h-5 ml-2" />
        </Button>
        <p className="text-sm text-gray-500 mt-4">Takes approximately 10-15 minutes to complete</p>
      </div>
    </div>
  );

  // Render profile form
  const renderProfile = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
            <Building2 className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-gray-900">Company Profile</h2>
            <p className="text-sm text-gray-600">Tell us about your organization</p>
          </div>
        </div>

        <div className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Company Name *</label>
            <Input
              value={profile.company_name}
              onChange={(e) => setProfile({...profile, company_name: e.target.value})}
              placeholder="Enter company name"
              className="w-full"
              data-testid="input-company"
            />
          </div>

          {/* Company URL for scraping */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Company Website / AI Strategy Report URL
              <span className="text-xs text-gray-500 ml-1">(Optional - we'll analyze it)</span>
            </label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  value={profile.company_url}
                  onChange={(e) => setProfile({...profile, company_url: e.target.value})}
                  placeholder="https://company.com/ai-strategy"
                  className="w-full pr-10"
                  data-testid="input-company-url"
                />
                <Globe className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              </div>
              {profile.company_url && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={scrapeCompanyUrl}
                  disabled={scrapingUrl}
                  className="px-3"
                >
                  {scrapingUrl ? <Loader2 className="w-4 h-4 animate-spin" /> : <ExternalLink className="w-4 h-4" />}
                </Button>
              )}
            </div>
            {scrapedData?.success && (
              <p className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                <CheckCircle className="w-3 h-3" /> Report analyzed - insights will inform your assessment
              </p>
            )}
            {scrapedData?.success === false && (
              <p className="text-xs text-gray-500 mt-1">Could not analyze URL. Assessment will proceed normally.</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Industry *</label>
            <Select value={profile.industry} onValueChange={(v) => setProfile({...profile, industry: v})}>
              <SelectTrigger className="w-full" data-testid="select-industry">
                <SelectValue placeholder="Select industry" />
              </SelectTrigger>
              <SelectContent>
                {industries.map((ind) => (
                  <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Company Size *</label>
              <Select value={profile.company_size} onValueChange={(v) => setProfile({...profile, company_size: v})}>
                <SelectTrigger className="w-full" data-testid="select-size">
                  <SelectValue placeholder="Select size" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="1-50">1-50 employees</SelectItem>
                  <SelectItem value="51-200">51-200 employees</SelectItem>
                  <SelectItem value="201-1000">201-1,000 employees</SelectItem>
                  <SelectItem value="1001-5000">1,001-5,000 employees</SelectItem>
                  <SelectItem value="5001-10000">5,001-10,000 employees</SelectItem>
                  <SelectItem value="10001+">10,001+ employees</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Annual Revenue *</label>
              <Select value={profile.annual_revenue} onValueChange={(v) => setProfile({...profile, annual_revenue: v})}>
                <SelectTrigger className="w-full" data-testid="select-revenue">
                  <SelectValue placeholder="Select revenue" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="<$1M">Less than $1 Million</SelectItem>
                  <SelectItem value="$1M-$10M">$1M - $10 Million</SelectItem>
                  <SelectItem value="$10M-$100M">$10M - $100 Million</SelectItem>
                  <SelectItem value="$100M-$500M">$100M - $500 Million</SelectItem>
                  <SelectItem value="$500M-$1B">$500M - $1 Billion</SelectItem>
                  <SelectItem value=">$1B">Over $1 Billion</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="border-t pt-5 mt-5">
            <h3 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
              <User className="w-4 h-4" />
              Respondent Information
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Your Name *</label>
                  <Input
                    value={profile.respondent_name}
                    onChange={(e) => setProfile({...profile, respondent_name: e.target.value})}
                    placeholder="Full name"
                    data-testid="input-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title *</label>
                  <Input
                    value={profile.respondent_role}
                    onChange={(e) => setProfile({...profile, respondent_role: e.target.value})}
                    placeholder="e.g., CTO, VP Engineering"
                    data-testid="input-role"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Address *</label>
                <Input
                  type="email"
                  value={profile.respondent_email}
                  onChange={(e) => setProfile({...profile, respondent_email: e.target.value})}
                  placeholder="your.email@company.com"
                  data-testid="input-email"
                />
                <p className="text-xs text-gray-500 mt-1">Your secret code will be sent to this email address</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  // Render dimension questions
  const renderDimension = (dimension) => {
    const IconComponent = dimensionIcons[dimension.icon] || Target;
    
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
          {/* Dimension Header */}
          <div className="flex items-center gap-4 mb-6 pb-6 border-b">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center">
              <IconComponent className="w-7 h-7 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">{dimension.name}</h2>
              <p className="text-sm text-gray-600">{dimension.description}</p>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-8">
            {dimension.questions.map((question, qIdx) => (
              <div key={question.id} className="space-y-4">
                <div className="flex items-start gap-3">
                  <span className="flex-shrink-0 w-7 h-7 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-sm font-semibold">
                    {qIdx + 1}
                  </span>
                  <p className="text-gray-800 font-medium leading-relaxed">{question.text}</p>
                </div>
                <div className="grid gap-2 pl-10">
                  {question.options.map((option) => (
                    <button
                      key={option.value}
                      onClick={() => setResponses({...responses, [question.id]: option.value})}
                      className={`w-full text-left p-4 rounded-lg border-2 transition-all ${
                        responses[question.id] === option.value
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300 hover:bg-gray-50'
                      }`}
                      data-testid={`option-${question.id}-${option.value}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          responses[question.id] === option.value
                            ? 'border-indigo-500 bg-indigo-500'
                            : 'border-gray-300'
                        }`}>
                          {responses[question.id] === option.value && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-0.5 rounded ${
                              responses[question.id] === option.value
                                ? 'bg-indigo-200 text-indigo-700'
                                : 'bg-gray-200 text-gray-600'
                            }`}>
                              Level {option.value}
                            </span>
                            <span className="font-medium text-gray-900">{option.label}</span>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{option.description}</p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Additional Context Section - Show only on last dimension before generating report */}
        {isLastDimension && (
          <div className="mt-8">
            <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl overflow-hidden">
              <button
                onClick={() => setShowContextInput(!showContextInput)}
                className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-white/50 transition-colors"
                data-testid="toggle-context-input"
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-lg flex items-center justify-center">
                    <MessageSquare className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Additional Context (Optional)</h4>
                    <p className="text-sm text-gray-600">Share specific AI challenges, goals, or context for personalized insights</p>
                  </div>
                </div>
                {showContextInput ? (
                  <ChevronUp className="w-5 h-5 text-gray-500" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-gray-500" />
                )}
              </button>
              {showContextInput && (
                <div className="px-6 pb-6">
                  <textarea
                    value={additionalContext}
                    onChange={(e) => setAdditionalContext(e.target.value)}
                    placeholder="Examples:&#10;• We are exploring Generative AI for customer support automation&#10;• Our data infrastructure is not ready for ML workloads&#10;• We have limited AI/ML talent in-house&#10;• We need to comply with AI regulations (EU AI Act, GDPR)&#10;• We want to build an AI Center of Excellence&#10;• Budget constraints for AI initiatives"
                    className="w-full h-32 p-4 border border-gray-200 rounded-lg resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-700 placeholder:text-gray-400"
                    maxLength={1500}
                    data-testid="additional-context-input"
                  />
                  <div className="flex justify-between items-center mt-2">
                    <p className="text-xs text-gray-500">
                      This context helps our AI provide more relevant recommendations for your AI journey
                    </p>
                    <span className="text-xs text-gray-400">{additionalContext.length}/1500</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Render chatbot
  const renderChatbot = () => (
    <div className={`fixed bottom-4 right-4 z-50 transition-all duration-300 ${showChatbot ? 'w-96' : 'w-auto'}`}>
      {showChatbot ? (
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-white">AI Consultant</h3>
                <p className="text-xs text-white/70">Ask me anything about AI maturity</p>
              </div>
            </div>
            <button
              onClick={() => setShowChatbot(false)}
              className="text-white/70 hover:text-white"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>
          
          {/* Chat Messages */}
          <div className="h-80 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {chatMessages.length === 0 && (
              <div className="text-center text-gray-500 py-8">
                <Bot className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm">Hi! I&apos;m here to help gather additional context for your AI maturity assessment.</p>
                <p className="text-xs mt-2">Tell me about your AI initiatives, challenges, or goals.</p>
              </div>
            )}
            {chatMessages.map((msg, idx) => (
              <div key={idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-lg p-3 ${
                  msg.role === 'user'
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white border border-gray-200 text-gray-800'
                }`}>
                  <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <Loader2 className="w-4 h-4 animate-spin text-indigo-600" />
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>
          
          {/* Chat Input */}
          <div className="p-4 border-t bg-white">
            <div className="flex gap-2">
              <Input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type your message..."
                className="flex-1"
                data-testid="chat-input"
              />
              <Button
                onClick={sendChatMessage}
                disabled={chatLoading || !chatInput.trim()}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="chat-send"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowChatbot(true)}
          className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-4 rounded-full shadow-lg hover:shadow-xl transition-all flex items-center gap-2"
          data-testid="open-chatbot"
        >
          <Bot className="w-6 h-6" />
          <span className="font-medium">AI Consultant</span>
        </button>
      )}
    </div>
  );

  // Render results page
  const renderResults = () => renderAssessmentResults({ ...results, ...results?.sample_results, ...(fullResults || {}) });
  const renderAssessmentResults = (results) => {
    if (!results) return null;
    
    const levelNum = results.maturity_level?.level_number || 0;
    const levelConfig = maturityLevelConfig[levelNum] || maturityLevelConfig[0];
    const benchmark = aiBenchmark(results);
    
    return (
      <div className="w-full min-w-0 max-w-5xl mx-auto" data-testid="ai-results">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br ${levelConfig.color} mb-4 shadow-lg`}>
            <Brain className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">AI Maturity Assessment Results</h1>
          <p className="text-gray-600">{results.company_name} - {results.industry}</p>
          
          {/* Download Button - Prominent CTA */}
          {fullResults && (
            <div className="mt-6">
              <Button
                onClick={() => generateAIMaturityPptx(results, fullResults)}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white w-full sm:w-auto max-w-full h-auto min-h-11 whitespace-normal px-3 sm:px-8 py-3 text-sm sm:text-lg shadow-lg"
                data-testid="btn-download-pptx"
              >
                <FileText className="w-5 h-5 mr-2" />
                Download Executive Presentation
              </Button>
              <p className="text-sm text-gray-500 mt-2">PowerPoint presentation ready for CXO briefing</p>
            </div>
          )}
        </div>

        {/* Score Overview */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          {/* Overall Score */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
            <h3 className="text-sm font-medium text-gray-600 mb-4">Your AI Maturity Score</h3>
            <div className="text-center">
              <div data-testid="ai-overall-score" className={`text-5xl font-bold bg-gradient-to-r ${levelConfig.color} bg-clip-text text-transparent`}>
                {metricNumber(results.overall_score)?.toFixed(1) ?? 'Not available'}
              </div>
              <div className="text-gray-500 text-sm mt-1">out of 5.0</div>
              <div className={`inline-block mt-3 px-4 py-2 rounded-full ${levelConfig.bg} ${levelConfig.text} font-semibold`}>
                {results.maturity_level?.level}
              </div>
            </div>
          </div>

          {/* Industry Comparison */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
            <h3 className="text-sm font-medium text-gray-600 mb-4">Illustrative Industry Comparison</h3>
            <div className="space-y-4">
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Industry Average</span>
                  <span data-testid="ai-industry-average" className="font-semibold">{benchmark.avg === null ? 'Not available' : `${benchmark.avg}/5.0`}</span>
                </div>
                <Progress value={(benchmark.avg / 5) * 100} className="h-2" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Top Performers</span>
                  <span data-testid="ai-top-benchmark" className="font-semibold">{benchmark.top === null ? 'Not available' : `${benchmark.top}/5.0`}</span>
                </div>
                <Progress value={(benchmark.top / 5) * 100} className="h-2 bg-gray-200" />
              </div>
              <div>
                <div className="flex justify-between text-sm mb-1">
                  <span className="text-gray-600">Your Score</span>
                  <span className="font-semibold text-indigo-600">{results.overall_score?.toFixed(1)}/5.0</span>
                </div>
                <Progress value={(results.overall_score / 5) * 100} className="h-2 bg-indigo-100" />
              </div>
            </div>
          </div>

          {/* Gap Analysis */}
          <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
            <h3 className="text-sm font-medium text-gray-600 mb-4">Gap Analysis</h3>
            <div className="space-y-3">
              <div className={`p-3 rounded-lg ${results.overall_score >= benchmark.avg ? 'bg-green-50' : 'bg-red-50'}`}>
                <div className="text-xs text-gray-600">Gap to Industry Average</div>
                <div data-testid="ai-gap-average" className={`text-xl font-bold ${benchmark.avg === null ? 'text-gray-600' : results.overall_score >= benchmark.avg ? 'text-green-600' : 'text-red-600'}`}>
                  {gapText(results.overall_score, benchmark.avg, true)}
                </div>
              </div>
              <div className="p-3 rounded-lg bg-amber-50">
                <div className="text-xs text-gray-600">Gap to Top Performers</div>
                <div data-testid="ai-gap-top" className="text-xl font-bold text-amber-600">
                  {gapText(results.overall_score, benchmark.top)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Dimension Scores - Basic View */}
        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg mb-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-6">Dimension Analysis</h3>
          <div className="grid md:grid-cols-2 gap-4">
            {results.dimension_scores?.map((dim) => {
              const benchmarkAvg = benchmark.avg;
              const status = getScoreStatus(dim.score, benchmarkAvg);
              const IconComponent = dimensionIcons[AI_ASSESSMENT_DIMENSIONS?.[dim.dimension_id]?.icon] || Target;
              const gapValue = gapText(dim.score, benchmarkAvg);
              return (
                <div key={dim.dimension_id} className={`flex items-center gap-4 p-4 rounded-lg ${status.color === 'text-red-600' ? 'bg-red-50' : status.color === 'text-amber-600' ? 'bg-amber-50' : 'bg-gray-50'}`}>
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0 ${status.color === 'text-red-600' ? 'bg-gradient-to-br from-red-500 to-red-600' : status.color === 'text-amber-600' ? 'bg-gradient-to-br from-amber-500 to-amber-600' : 'bg-gradient-to-br from-indigo-500 to-purple-500'}`}>
                    <IconComponent className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center justify-between gap-1 mb-1">
                      <span className="font-medium text-gray-900 break-words">{dim.dimension_name}</span>
                      <span className={`text-xs px-2 py-1 rounded-full ${status.bg} ${status.color} font-medium`}>
                        {status.status}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Progress value={(dim.score / 5) * 100} className="flex-1 h-2" />
                      <span className="text-sm font-semibold text-gray-700 w-12">{dim.score}/5</span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      Gap: <span className={benchmarkAvg === null ? 'text-gray-600' : parseFloat(gapValue) < 0 ? 'text-red-600' : 'text-green-600'}>{gapValue}</span> vs benchmark ({benchmarkAvg ?? 'Not available'})
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Code Verification */}
        {!fullResults ? (
          <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-8 mb-8">
            <div className="max-w-md mx-auto text-center">
              <div className="w-16 h-16 rounded-full bg-indigo-100 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-8 h-8 text-indigo-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">Unlock Full Report</h3>
              <p className="text-gray-600 mb-6">
                A secret code has been sent to <strong>{profile.respondent_email || results?.respondent_email || 'your email'}</strong>. 
                Enter it below to access your complete AI Maturity Assessment report with 
                detailed recommendations and 12-month roadmap.
              </p>
              <div className="flex gap-3 justify-center">
                <Input
                  value={secretCode}
                  onChange={(e) => setSecretCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="Enter 6-digit code"
                  className="w-40 text-center text-lg font-mono tracking-wider"
                  data-testid="input-secret-code"
                />
                <Button
                  onClick={verifyCode}
                  disabled={verifyingCode || secretCode.length !== 6}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="btn-verify-code"
                >
                  {verifyingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Unlock'}
                </Button>
              </div>
              {codeError && (
                <p className="text-red-600 text-sm mt-3 flex items-center justify-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {codeError}
                </p>
              )}
            </div>
          </div>
        ) : (
          /* Full Results - Comprehensive Report */
          <div className="space-y-8">
            {/* Executive Summary - FIRST */}
            <div className="bg-gradient-to-br from-indigo-50 to-purple-50 border border-indigo-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-indigo-600" />
                Executive Summary
              </h3>
              <div className="prose prose-indigo max-w-none">
                {(() => {
                  const execSummary = fullResults.ai_analysis?.executive_summary;
                  const rawAnalysis = fullResults.ai_analysis?.raw_analysis;
                  
                  if (typeof execSummary === 'string' && execSummary.length > 50) {
                    return execSummary.split('\n').filter(p => p.trim()).map((para, i) => (
                      <p key={i} className="text-gray-700 mb-3 leading-relaxed">{para}</p>
                    ));
                  } else if (typeof rawAnalysis === 'string' && rawAnalysis.length > 50) {
                    return rawAnalysis.substring(0, 2000).split('\n').filter(p => p.trim()).map((para, i) => (
                      <p key={i} className="text-gray-700 mb-3 leading-relaxed">{para}</p>
                    ));
                  } else {
                    // Generate default executive summary
                    const score = fullResults.overall_score?.toFixed(1) || '2.0';
                    const level = fullResults.maturity_level?.level || 'Predictive AI';
                    const industry = fullResults.industry || 'Technology';
                    const benchAvg = fullResults.industry_benchmark?.avg || 2.5;
                    const gap = (parseFloat(score) - benchAvg).toFixed(2);
                    const gapStatus = parseFloat(gap) < 0 ? 'below' : 'above';
                    
                    return (
                      <>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          <strong>{fullResults.company_name}</strong> has achieved an AI Maturity score of <strong>{score}/5.0</strong>, 
                          placing the organization at the <strong>{level}</strong> level. This score is <strong>{Math.abs(parseFloat(gap))} points {gapStatus}</strong> the 
                          {industry} industry average of {benchAvg}.
                        </p>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          The assessment reveals key strengths in foundational AI capabilities while identifying critical gaps in 
                          {parseFloat(gap) < 0 ? ' MLOps infrastructure, AI talent development, and enterprise-wide AI governance' : ' advanced AI scaling and multi-agent coordination'}. 
                          To advance to the next maturity level, focused investments in data infrastructure, model operations, and organizational change management are recommended.
                        </p>
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          Based on the 12-month transformation roadmap outlined below, the organization can expect to improve its AI maturity 
                          score by 1.0-1.5 points, achieving competitive parity with industry top performers while realizing an estimated 
                          ROI of 3-5x on AI investments.
                        </p>
                      </>
                    );
                  }
                })()}
              </div>
              
              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6 pt-6 border-t border-indigo-200">
                <div className="text-center">
                  <div className="text-2xl font-bold text-indigo-600">{fullResults.overall_score?.toFixed(1)}/5.0</div>
                  <div className="text-xs text-gray-500">Your Score</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-600">{fullResults.industry_benchmark?.avg || 2.5}</div>
                  <div className="text-xs text-gray-500">Industry Avg</div>
                </div>
                <div className="text-center">
                  <div className={`text-2xl font-bold ${fullResults.overall_score >= (fullResults.industry_benchmark?.avg || 2.5) ? 'text-green-600' : 'text-red-600'}`}>
                    {((fullResults.overall_score || 0) - (fullResults.industry_benchmark?.avg || 2.5)).toFixed(2)}
                  </div>
                  <div className="text-xs text-gray-500">Gap to Avg</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-amber-600">{((fullResults.industry_benchmark?.top || 4.0) - (fullResults.overall_score || 0)).toFixed(2)}</div>
                  <div className="text-xs text-gray-500">Gap to Top</div>
                </div>
              </div>
            </div>

            {/* 6 Comprehensive Dimensions Analysis */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <PieChart className="w-5 h-5 text-purple-600" />
                6 Comprehensive Dimensions Analysis
              </h3>
              <div className="space-y-4">
                {fullResults.dimension_scores?.map((dim) => {
                  const benchmarkAvg = fullResults.industry_benchmark?.avg || 2.5;
                  const status = getScoreStatus(dim.score, benchmarkAvg);
                  const dimAnalysis = fullResults.ai_analysis?.dimension_analysis?.[dim.dimension_id] || 
                                      fullResults.ai_analysis?.dimension_analysis?.find?.(d => d.dimension === dim.dimension_name) ||
                                      (Array.isArray(fullResults.ai_analysis?.dimension_analysis) 
                                        ? fullResults.ai_analysis?.dimension_analysis.find(d => d.dimension_name === dim.dimension_name || d.dimension === dim.dimension_name)
                                        : null);
                  
                  // Generate improvement suggestions based on score gap
                  const gapToBenchmark = benchmarkAvg - dim.score;
                  const defaultImprovements = gapToBenchmark > 0 ? [
                    `Increase ${dim.dimension_name.toLowerCase()} maturity by ${gapToBenchmark.toFixed(1)} points to reach industry average`,
                    `Implement structured improvement program with 90-day milestones`,
                    `Allocate dedicated resources and budget for capability building`
                  ] : [
                    `Maintain current excellence and share best practices across organization`,
                    `Focus on continuous optimization and innovation`,
                    `Consider advanced capabilities for competitive advantage`
                  ];
                  
                  return (
                    <div key={dim.dimension_id} className={`border-2 rounded-xl p-4 ${status.color === 'text-red-600' ? 'border-red-200 bg-red-50/30' : status.color === 'text-amber-600' ? 'border-amber-200 bg-amber-50/30' : 'border-gray-200'}`}>
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${status.color === 'text-red-600' ? 'from-red-500 to-red-600' : status.color === 'text-amber-600' ? 'from-amber-500 to-amber-600' : 'from-indigo-500 to-purple-500'} flex items-center justify-center`}>
                            <Target className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">{dim.dimension_name}</h4>
                            <span className={`text-xs px-2 py-1 rounded-full ${status.bg} ${status.color} font-medium`}>
                              {status.status}
                            </span>
                          </div>
                        </div>
                        <div className="text-right">
                          <div className={`text-2xl font-bold ${status.color === 'text-red-600' ? 'text-red-600' : status.color === 'text-amber-600' ? 'text-amber-600' : 'text-indigo-600'}`}>{dim.score}/5.0</div>
                          <div className="text-xs text-gray-500">{dim.maturity_level}</div>
                          <div className="text-xs text-gray-400">Benchmark: {benchmarkAvg}</div>
                        </div>
                      </div>
                      <Progress value={(dim.score / 5) * 100} className="h-2 mb-3" />
                      
                      {/* Strengths, Gaps, and Improvements */}
                      <div className="grid md:grid-cols-3 gap-4 mt-4 pt-4 border-t">
                        {/* Strengths */}
                        <div>
                          <h5 className="text-sm font-medium text-green-700 mb-2 flex items-center gap-1">
                            <CheckCircle className="w-4 h-4" /> Strengths
                          </h5>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {(dimAnalysis?.strengths && (Array.isArray(dimAnalysis.strengths) ? dimAnalysis.strengths : [dimAnalysis.strengths]).length > 0
                              ? (Array.isArray(dimAnalysis.strengths) ? dimAnalysis.strengths : [dimAnalysis.strengths]).slice(0, 2)
                              : ['Building foundational capabilities', 'Awareness of improvement areas']
                            ).map((s, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-green-500 mt-1">•</span>
                                <span>{typeof s === 'string' ? s : s.description || s.strength || JSON.stringify(s)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* Gaps */}
                        <div>
                          <h5 className="text-sm font-medium text-red-700 mb-2 flex items-center gap-1">
                            <AlertCircle className="w-4 h-4" /> Gaps
                          </h5>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {(dimAnalysis?.gaps && (Array.isArray(dimAnalysis.gaps) ? dimAnalysis.gaps : [dimAnalysis.gaps]).length > 0
                              ? (Array.isArray(dimAnalysis.gaps) ? dimAnalysis.gaps : [dimAnalysis.gaps]).slice(0, 2)
                              : gapToBenchmark > 0 
                                ? [`Score ${gapToBenchmark.toFixed(1)} below benchmark`, 'Process maturity needs improvement']
                                : ['Minor optimization opportunities', 'Continue monitoring for risks']
                            ).map((g, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-red-500 mt-1">•</span>
                                <span>{typeof g === 'string' ? g : g.description || g.gap || JSON.stringify(g)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                        
                        {/* How to Improve */}
                        <div>
                          <h5 className="text-sm font-medium text-indigo-700 mb-2 flex items-center gap-1">
                            <Lightbulb className="w-4 h-4" /> How to Improve
                          </h5>
                          <ul className="text-sm text-gray-600 space-y-1">
                            {(dimAnalysis?.recommendations && (Array.isArray(dimAnalysis.recommendations) ? dimAnalysis.recommendations : [dimAnalysis.recommendations]).length > 0
                              ? (Array.isArray(dimAnalysis.recommendations) ? dimAnalysis.recommendations : [dimAnalysis.recommendations]).slice(0, 2)
                              : defaultImprovements.slice(0, 2)
                            ).map((r, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="text-indigo-500 mt-1">•</span>
                                <span>{typeof r === 'string' ? r : r.description || r.recommendation || JSON.stringify(r)}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Industry Benchmarking */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-emerald-600" />
                Industry Benchmarking - {fullResults.industry}
              </h3>
              <div className="grid md:grid-cols-4 gap-4 mb-6">
                <div className="bg-indigo-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Your Score</p>
                  <p className="text-3xl font-bold text-indigo-600">{fullResults.overall_score?.toFixed(1)}</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Industry Average</p>
                  <p className="text-3xl font-bold text-gray-700">{fullResults.industry_benchmark?.avg || 2.5}</p>
                </div>
                <div className="bg-emerald-50 rounded-xl p-4 text-center">
                  <p className="text-xs text-gray-500 mb-1">Top Performers</p>
                  <p className="text-3xl font-bold text-emerald-600">{fullResults.industry_benchmark?.top || 4.0}</p>
                </div>
                <div className={`rounded-xl p-4 text-center ${fullResults.overall_score >= (fullResults.industry_benchmark?.avg || 2.5) ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="text-xs text-gray-500 mb-1">Gap to Average</p>
                  <p className={`text-3xl font-bold ${fullResults.overall_score >= (fullResults.industry_benchmark?.avg || 2.5) ? 'text-green-600' : 'text-red-600'}`}>
                    {((fullResults.overall_score || 0) - (fullResults.industry_benchmark?.avg || 2.5)).toFixed(2)}
                  </p>
                </div>
              </div>
              
              {/* Competitive Position - Fixed formatting */}
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-medium text-gray-900 mb-3">Competitive Position</h4>
                <div className="text-sm text-gray-600 space-y-2">
                  {(() => {
                    const benchmarkText = formatBenchmarkText(fullResults.ai_analysis?.benchmark_comparison);
                    if (benchmarkText) {
                      return <p>{benchmarkText}</p>;
                    }
                    
                    // Generate default competitive position text
                    const score = fullResults.overall_score || 2.0;
                    const avg = fullResults.industry_benchmark?.avg || 2.5;
                    const top = fullResults.industry_benchmark?.top || 4.0;
                    const industry = fullResults.industry || 'your industry';
                    
                    return (
                      <>
                        <p>
                          {score >= avg 
                            ? `Your organization is performing at or above the ${industry} industry average, demonstrating solid AI maturity foundations.`
                            : `Your organization is currently ${(avg - score).toFixed(1)} points below the ${industry} industry average of ${avg}, indicating opportunities for improvement.`
                          }
                        </p>
                        <p>
                          The gap to top performers ({top}) is {(top - score).toFixed(1)} points. 
                          {score < avg 
                            ? ' Focused investment in AI capabilities can help close this gap and improve competitive positioning.'
                            : ' Continuing to invest in advanced AI capabilities will help maintain and extend your competitive advantage.'
                          }
                        </p>
                        <p>
                          Industry trends show increasing adoption of {score < 2 ? 'predictive analytics and ML automation' : score < 3 ? 'generative AI and intelligent automation' : 'agentic AI and multi-agent systems'} across {industry} organizations.
                        </p>
                      </>
                    );
                  })()}
                </div>
              </div>
            </div>

            {/* 12-Month Transformation Roadmap */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Rocket className="w-5 h-5 text-purple-600" />
                12-Month Transformation Roadmap
              </h3>
              {(() => {
                // Get roadmap data or generate default
                const roadmapData = fullResults.ai_analysis?.transformation_roadmap || 
                                   fullResults.ai_analysis?.implementation_roadmap ||
                                   getDefaultRoadmap(fullResults.industry, fullResults.overall_score || 2.0);
                
                const phases = Array.isArray(roadmapData) ? roadmapData : Object.values(roadmapData || {});
                
                if (phases.length === 0) {
                  // Use default roadmap if none available
                  const defaultPhases = getDefaultRoadmap(fullResults.industry, fullResults.overall_score || 2.0);
                  return (
                    <div className="space-y-4">
                      {defaultPhases.map((phase, i) => (
                        <div key={i} className={`border-2 rounded-xl p-4 hover:shadow-md transition-shadow ${i === 0 ? 'border-indigo-300 bg-indigo-50/30' : i === 1 ? 'border-purple-300 bg-purple-50/30' : i === 2 ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
                          <div className="flex items-center gap-3 mb-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${i === 0 ? 'bg-indigo-500' : i === 1 ? 'bg-purple-500' : i === 2 ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                              {i + 1}
                            </div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900">{phase.phase_name}</div>
                              <div className="text-sm text-indigo-600">{phase.duration}</div>
                            </div>
                            <div className="text-right">
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {phase.investment_range}
                              </span>
                            </div>
                          </div>
                          <div className="mb-3">
                            <h5 className="text-xs font-medium text-gray-500 mb-1">Focus Areas</h5>
                            <div className="flex flex-wrap gap-2">
                              {phase.focus_areas.map((area, j) => (
                                <span key={j} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                                  {area}
                                </span>
                              ))}
                            </div>
                          </div>
                          <div className="space-y-2">
                            <h5 className="text-xs font-medium text-gray-500">Key Initiatives</h5>
                            {phase.key_initiatives.map((init, j) => (
                              <div key={j} className="text-sm text-gray-600 flex items-start gap-2 bg-white/70 p-2 rounded">
                                <ChevronRight className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                                <span>{init}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                }
                
                return (
                  <div className="space-y-4">
                    {phases.map((phase, i) => (
                      <div key={i} className={`border-2 rounded-xl p-4 hover:shadow-md transition-shadow ${i === 0 ? 'border-indigo-300 bg-indigo-50/30' : i === 1 ? 'border-purple-300 bg-purple-50/30' : i === 2 ? 'border-emerald-300 bg-emerald-50/30' : 'border-amber-300 bg-amber-50/30'}`}>
                        <div className="flex items-center gap-3 mb-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold ${i === 0 ? 'bg-indigo-500' : i === 1 ? 'bg-purple-500' : i === 2 ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                            {i + 1}
                          </div>
                          <div className="flex-1">
                            <div className="font-semibold text-gray-900">{phase.phase_name || phase.phase || phase.name || `Phase ${i + 1}`}</div>
                            <div className="text-sm text-indigo-600">{phase.duration || phase.timeline || phase.timeframe}</div>
                          </div>
                          {(phase.investment_range || phase.investment || phase.budget) && (
                            <div className="text-right">
                              <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                                {phase.investment_range || phase.investment || phase.budget}
                              </span>
                            </div>
                          )}
                        </div>
                        {(phase.focus_areas || phase.areas || phase.objectives) && (
                          <div className="mb-3">
                            <h5 className="text-xs font-medium text-gray-500 mb-1">Focus Areas</h5>
                            <div className="flex flex-wrap gap-2">
                              {(Array.isArray(phase.focus_areas || phase.areas || phase.objectives) 
                                ? (phase.focus_areas || phase.areas || phase.objectives) 
                                : [phase.focus_areas || phase.areas || phase.objectives]
                              ).map((area, j) => (
                                <span key={j} className="text-xs bg-indigo-100 text-indigo-700 px-2 py-1 rounded-full">
                                  {typeof area === 'string' ? area : area.name || area.area || JSON.stringify(area)}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                        {(phase.key_initiatives || phase.initiatives || phase.activities) && (
                          <div className="space-y-2">
                            <h5 className="text-xs font-medium text-gray-500">Key Initiatives</h5>
                            {(Array.isArray(phase.key_initiatives || phase.initiatives || phase.activities) 
                              ? (phase.key_initiatives || phase.initiatives || phase.activities) 
                              : [phase.key_initiatives || phase.initiatives || phase.activities]
                            ).slice(0, 4).map((init, j) => (
                              <div key={j} className="text-sm text-gray-600 flex items-start gap-2 bg-white/70 p-2 rounded">
                                <ChevronRight className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
                                <span>{typeof init === 'string' ? init : init.initiative_name || init.name || init.description || init.activity || JSON.stringify(init)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Investment & ROI Analysis */}
            <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Investment & ROI Analysis
              </h3>
              {fullResults.ai_analysis?.investment_priorities || fullResults.ai_analysis?.investment_roi ? (
                <div className="space-y-4">
                  {(fullResults.ai_analysis.investment_priorities || fullResults.ai_analysis.investment_roi || []).slice(0, 5).map((inv, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="font-medium text-gray-900">{inv.area || inv.initiative || `Investment Area ${i + 1}`}</h4>
                        {inv.investment_required && (
                          <span className="text-sm font-medium text-green-600">{inv.investment_required}</span>
                        )}
                      </div>
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        {inv.current_state && (
                          <div>
                            <span className="text-gray-500">Current State:</span>
                            <span className="ml-2 text-gray-700">{inv.current_state}</span>
                          </div>
                        )}
                        {inv.target_state && (
                          <div>
                            <span className="text-gray-500">Target State:</span>
                            <span className="ml-2 text-gray-700">{inv.target_state}</span>
                          </div>
                        )}
                        {inv.expected_roi && (
                          <div>
                            <span className="text-gray-500">Expected ROI:</span>
                            <span className="ml-2 text-green-600 font-medium">{inv.expected_roi}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="bg-blue-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Estimated Investment</p>
                    <p className="text-xl font-bold text-blue-600">$500K - $2M</p>
                    <p className="text-xs text-gray-500 mt-1">12-month program</p>
                  </div>
                  <div className="bg-green-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Expected ROI</p>
                    <p className="text-xl font-bold text-green-600">3-5x</p>
                    <p className="text-xs text-gray-500 mt-1">Within 24 months</p>
                  </div>
                  <div className="bg-purple-50 rounded-xl p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">Payback Period</p>
                    <p className="text-xl font-bold text-purple-600">12-18 months</p>
                    <p className="text-xs text-gray-500 mt-1">Post implementation</p>
                  </div>
                </div>
              )}
            </div>

            {/* AI Use Case Prioritization */}
            {fullResults.ai_analysis?.ai_use_cases && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Lightbulb className="w-5 h-5 text-amber-600" />
                  Recommended AI Use Cases for {fullResults.industry}
                </h3>
                <div className="space-y-3">
                  {(Array.isArray(fullResults.ai_analysis.ai_use_cases) 
                    ? fullResults.ai_analysis.ai_use_cases 
                    : Object.values(fullResults.ai_analysis.ai_use_cases)
                  ).slice(0, 5).map((useCase, i) => (
                    <div key={i} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{useCase.use_case_name || useCase.name}</h4>
                          <p className="text-sm text-gray-600 mt-1">{useCase.description}</p>
                        </div>
                        <div className="flex gap-2 ml-4">
                          {useCase.business_impact && (
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              useCase.business_impact === 'High' ? 'bg-green-100 text-green-700' :
                              useCase.business_impact === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                              'bg-gray-100 text-gray-700'
                            }`}>
                              {useCase.business_impact} Impact
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Areas of Improvement */}
            {fullResults.ai_analysis?.areas_of_improvement && (
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                  Areas of Improvement
                </h3>
                
                {/* Critical Gaps - RED */}
                {fullResults.ai_analysis.areas_of_improvement.critical_gaps?.length > 0 && (
                  <div className="mb-6">
                    <h4 className="text-sm font-semibold text-red-600 mb-3 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-red-500"></div>
                      Critical Gaps (Immediate Action Required)
                    </h4>
                    <div className="space-y-3">
                      {fullResults.ai_analysis.areas_of_improvement.critical_gaps.map((gap, i) => (
                        <div key={i} className="p-4 rounded-lg bg-red-50 border border-red-200">
                          <div className="font-medium text-red-800">{gap.area_name || gap}</div>
                          {gap.solution_approach && (
                            <p className="text-sm text-red-700 mt-1">{gap.solution_approach}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* High Priority - AMBER */}
                {fullResults.ai_analysis.areas_of_improvement.high_priority?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-amber-600 mb-3 flex items-center gap-2">
                      <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                      High Priority Improvements
                    </h4>
                    <div className="space-y-3">
                      {fullResults.ai_analysis.areas_of_improvement.high_priority.map((item, i) => (
                        <div key={i} className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                          <div className="font-medium text-amber-800">{item.area_name || item}</div>
                          {item.solution_approach && (
                            <p className="text-sm text-amber-700 mt-1">{item.solution_approach}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Level Up Section - Journey to Next AI Maturity Level */}
            {fullResults.ai_analysis?.level_progression && (
              <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-2xl shadow-lg p-8 border-2 border-violet-200" data-testid="level-up-section">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-purple-600 rounded-xl flex items-center justify-center">
                    <ArrowUpRight className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Level Up: Journey to Next AI Maturity Level</h2>
                    <p className="text-gray-600">What it takes to advance your AI capabilities</p>
                  </div>
                </div>

                {/* Current to Next Level Visualization */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                  <div className="flex items-center justify-between mb-6">
                    <div className="text-center flex-1">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-amber-400 to-orange-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {fullResults.ai_analysis.level_progression.current_level?.level_number || Math.floor(fullResults.overall_score)}
                        </span>
                      </div>
                      <div className="font-bold text-gray-900">Current Level</div>
                      <div className="text-sm text-gray-500">
                        {fullResults.ai_analysis.level_progression.current_level?.level_name || fullResults.maturity_level}
                      </div>
                    </div>
                    <div className="flex-1 px-4">
                      <div className="relative">
                        <div className="h-2 bg-gradient-to-r from-amber-400 via-violet-400 to-violet-500 rounded-full" />
                        <div className="absolute top-4 left-1/2 transform -translate-x-1/2 text-center">
                          <div className="flex items-center gap-1 bg-violet-100 px-3 py-1 rounded-full">
                            <Clock className="w-3 h-3 text-violet-600" />
                            <span className="text-xs font-medium text-violet-700">
                              {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-12 months'}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="text-center flex-1">
                      <div className="w-20 h-20 mx-auto bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center mb-3 shadow-lg">
                        <span className="text-3xl font-bold text-white">
                          {fullResults.ai_analysis.level_progression.next_level?.level_number || Math.min(Math.floor(fullResults.overall_score) + 1, 5)}
                        </span>
                      </div>
                      <div className="font-bold text-gray-900">Target Level</div>
                      <div className="text-sm text-violet-600 font-medium">
                        {fullResults.ai_analysis.level_progression.next_level?.level_name || 'Next Level'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Resource Requirements Grid */}
                <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                        <Clock className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="font-bold text-gray-900">Time</div>
                    </div>
                    <div className="text-2xl font-bold text-blue-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.time_estimate || '6-12 months'}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                        <Users className="w-5 h-5 text-purple-600" />
                      </div>
                      <div className="font-bold text-gray-900">Manpower</div>
                    </div>
                    <div className="text-lg font-bold text-purple-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.manpower_resources?.internal_fte || 'Not available'}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-violet-100 rounded-lg flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-violet-600" />
                      </div>
                      <div className="font-bold text-gray-900">Investment</div>
                    </div>
                    <div className="text-lg font-bold text-violet-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.investment_required?.total_range || 'Not available'}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-5 shadow-sm border border-violet-100">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                        <TrendingUp className="w-5 h-5 text-amber-600" />
                      </div>
                      <div className="font-bold text-gray-900">Expected ROI</div>
                    </div>
                    <div className="text-lg font-bold text-amber-600 mb-1">
                      {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.quantified_value || 'Not available'}
                    </div>
                  </div>
                </div>

                {/* Milestones */}
                {fullResults.ai_analysis.level_progression.milestones?.length > 0 && (
                  <div className="bg-white rounded-xl p-3 sm:p-6 mb-8 shadow-sm">
                    <h3 className="font-bold text-gray-900 mb-6 flex items-center gap-2">
                      <Flag className="w-5 h-5 text-violet-600" />
                      Key Milestones to Next Level
                    </h3>
                    <div className="relative">
                      <div className="hidden sm:block absolute left-6 top-8 bottom-8 w-0.5 bg-gradient-to-b from-violet-400 to-purple-500" />
                      <div className="space-y-6">
                        {fullResults.ai_analysis.level_progression.milestones.map((milestone, idx) => (
                          <div key={idx} data-testid={`ai-milestone-${idx}`} className="relative flex flex-col sm:flex-row gap-3 sm:gap-4 min-w-0">
                            <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 z-10 bg-gradient-to-br from-violet-500 to-purple-600 shadow-lg">
                              <span className="text-white font-bold">{milestone.milestone_number || idx + 1}</span>
                            </div>
                            <div className="flex-1 min-w-0 w-full break-words bg-gradient-to-r from-violet-50 to-transparent rounded-lg p-4 border border-violet-100">
                              <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
                                <h4 className="font-bold text-gray-900">{milestone.name}</h4>
                                <span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-medium flex items-center gap-1">
                                  <Calendar className="w-3 h-3" />
                                  {milestone.target_month}
                                </span>
                              </div>
                              {milestone.key_deliverables?.length > 0 && (
                                <div className="text-sm text-gray-600 mb-2">
                                  <strong>Deliverables:</strong>
                                  <ul className="list-disc list-inside ml-2 mt-1">
                                    {milestone.key_deliverables.slice(0, 3).map((d, i) => (
                                      <li key={i} className="text-gray-600">{d}</li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                              {milestone.success_criteria && (
                                <div className="text-xs text-violet-600 font-medium">
                                  ✓ Success: {milestone.success_criteria}
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {/* Business Benefits at Next Level */}
                <div className="bg-white rounded-xl p-6 mb-8 shadow-sm">
                  <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-violet-600" />
                    Business Benefits at Next AI Level
                  </h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-emerald-50 to-white rounded-lg p-4 border border-emerald-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="w-4 h-4 text-emerald-600" />
                        <span className="text-sm font-medium text-gray-600">Efficiency Gains</span>
                      </div>
                      <div className="text-lg font-bold text-emerald-700">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.efficiency_gains || 'Not available'}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-blue-50 to-white rounded-lg p-4 border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-4 h-4 text-blue-600" />
                        <span className="text-sm font-medium text-gray-600">Revenue Impact</span>
                      </div>
                      <div className="text-lg font-bold text-blue-700">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.revenue_impact || 'Not available'}
                      </div>
                    </div>
                    <div className="bg-gradient-to-br from-amber-50 to-white rounded-lg p-4 border border-amber-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Shield className="w-4 h-4 text-amber-600" />
                        <span className="text-sm font-medium text-gray-600">Cost Reduction</span>
                      </div>
                      <div className="text-lg font-bold text-amber-700">
                        {fullResults.ai_analysis.level_progression.progression_requirements?.business_benefits?.risk_reduction || 'Not available'}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Key Challenges & Quick Wins */}
                <div className="grid md:grid-cols-2 gap-6">
                  {fullResults.ai_analysis.level_progression.key_challenges?.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-amber-100">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <AlertTriangle className="w-5 h-5 text-amber-600" />
                        Key Challenges
                      </h3>
                      <ul className="space-y-2">
                        {fullResults.ai_analysis.level_progression.key_challenges.map((challenge, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <div className="w-5 h-5 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                              <span className="text-amber-700 text-xs font-bold">{idx + 1}</span>
                            </div>
                            {challenge}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate?.length > 0 && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-violet-100">
                      <h3 className="font-bold text-gray-900 mb-4 flex items-center gap-2">
                        <Rocket className="w-5 h-5 text-violet-600" />
                        Quick Wins to Accelerate
                      </h3>
                      <ul className="space-y-2">
                        {fullResults.ai_analysis.level_progression.quick_wins_to_accelerate.map((win, idx) => (
                          <li key={idx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-5 h-5 text-violet-500 flex-shrink-0 mt-0.5" />
                            {win}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* AGI Insights */}
        <AGIInsightsTab assessmentType="AI Maturity" assessmentId={results && (results.id || results.assessment_id)} />

        <ReportSecurityNote />

        {/* Actions */}
        <div className="flex flex-wrap justify-center gap-4 mt-8">
          <Button variant="outline" onClick={() => navigate('/')} className="gap-2">
            <Home className="w-4 h-4" />
            Back to Home
          </Button>
          {fullResults && (
            <Button
              onClick={() => generateAIMaturityPptx(results, fullResults)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 gap-2"
              data-testid="btn-download-pptx-bottom"
            >
              <Download className="w-4 h-4" />
              Download Presentation
            </Button>
          )}
          <Button
            onClick={() => {
              setStep(0);
              setResults(null);
              setFullResults(null);
              setResponses({});
              setProfile({
                company_name: '',
                industry: '',
                company_size: '',
                annual_revenue: '',
                respondent_name: '',
                respondent_role: '',
                respondent_email: ''
              });
            }}
            className="bg-gray-800 hover:bg-gray-900 gap-2"
          >
            <ArrowRight className="w-4 h-4" />
            Start New Assessment
          </Button>
        </div>
      </div>
    );
  };

  // Main render
  if (loadingAssessment) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Loading Your Assessment...</h2>
          <p className="text-gray-500 mt-2">Please wait while we retrieve your results</p>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 animate-spin text-indigo-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900">Analyzing Your AI Maturity</h2>
          <p className="text-gray-600 mt-2">Generating your personalized assessment report...</p>
          <p className="text-sm text-gray-500 mt-1">This may take up to 2 minutes</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 py-8 px-4">
      {/* Navigation */}
      <div className="max-w-5xl mx-auto mb-6">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-600 hover:text-indigo-600 transition-colors">
          <Home className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>
      </div>

      {/* Progress Bar */}
      {step > 0 && step < totalSteps && (
        <div className="max-w-3xl mx-auto mb-8">
          <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
            <span>Step {step} of {totalSteps - 1}</span>
            <span>{Math.round(progressPercent)}% Complete</span>
          </div>
          <Progress value={progressPercent} className="h-2" />
        </div>
      )}

      {/* Content */}
      {step === 0 && renderIntro()}
      {step === 1 && renderProfile()}
      {step > 1 && step <= dimensions.length + 1 && dimensions[currentDimensionIndex] && 
        renderDimension(dimensions[currentDimensionIndex])}
      {(step === totalSteps || step >= 999) && renderResults()}

      {/* Error message */}
      {error && (
        <div className="max-w-2xl mx-auto mt-6">
          <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <AlertCircle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Navigation buttons */}
      {step > 0 && step < totalSteps && (
        <div className="max-w-3xl mx-auto mt-8 flex justify-between">
          <Button
            variant="outline"
            onClick={handleBack}
            className="px-6 py-3"
            data-testid="btn-back"
          >
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          
          {isLastDimension ? (
            <Button
              onClick={submitAssessment}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 px-8 py-3"
              data-testid="btn-submit"
            >
              Generate Report
              <Rocket className="w-4 h-4 ml-2" />
            </Button>
          ) : (
            <Button
              onClick={handleNext}
              className="bg-indigo-600 hover:bg-indigo-700 px-6 py-3"
              data-testid="btn-next"
            >
              Next
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
          )}
        </div>
      )}

      {/* Floating Chatbot - Show during steps 1-6 (not on last dimension since it's embedded there) */}
      {step > 0 && step < totalSteps && !isLastDimension && renderChatbot()}
    </div>
  );
};

// Export dimension config for use in results
const AI_ASSESSMENT_DIMENSIONS = {
  strategy_governance: { icon: 'Target' },
  data_readiness: { icon: 'Database' },
  model_performance: { icon: 'Brain' },
  mlops_scaling: { icon: 'Settings' },
  adoption_change: { icon: 'Users' },
  risk_compliance: { icon: 'Shield' }
};

export default AIMaturityPage;
